'use client';

import { forwardRef, useId, useLayoutEffect, useRef, useState, type ButtonHTMLAttributes } from 'react';
import { createPortal } from 'react-dom';
import RoomIcon, { type RoomIconName } from './RoomIcon';
import styles from './action-control.module.css';

type TooltipSide = 'top' | 'bottom';
type AnchorBounds = Pick<DOMRect, 'left' | 'top' | 'right' | 'bottom' | 'width'>;
type TooltipPosition = { left: number; top: number; side: TooltipSide };

/** Geometry shared by resize/scroll updates; all coordinates are in the layout viewport. */
export function placeActionTooltip(anchor: AnchorBounds, tip: { width: number; height: number }, viewport: { left: number; top: number; width: number; height: number }, preferred: TooltipSide): TooltipPosition {
  const margin = 8, gap = 8;
  const above = anchor.top - viewport.top - margin - gap;
  const below = viewport.top + viewport.height - margin - anchor.bottom - gap;
  const side = preferred === 'top' ? (above < tip.height && below > above ? 'bottom' : 'top') : (below < tip.height && above > below ? 'top' : 'bottom');
  const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(value, Math.max(min, max)));
  return {
    left: clamp(anchor.left + anchor.width / 2 - tip.width / 2, viewport.left + margin, viewport.left + viewport.width - margin - tip.width),
    top: clamp(side === 'top' ? anchor.top - gap - tip.height : anchor.bottom + gap, viewport.top + margin, viewport.top + viewport.height - margin - tip.height),
    side,
  };
}

export interface ActionControlProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon: RoomIconName;
  label: string;
  description?: string;
  showLabel?: boolean;
  tooltipSide?: TooltipSide;
  variant?: 'quiet' | 'primary';
}

export const ActionControl = forwardRef<HTMLButtonElement, ActionControlProps>(function ActionControl({ icon, label, description, children, showLabel = true, tooltipSide = 'top', variant = 'quiet', className, disabled, type = 'button', 'aria-label': accessibleName, 'aria-describedby': describedBy, ...buttonProps }, ref) {
  const tooltipId = useId();
  const anchor = useRef<HTMLSpanElement>(null);
  const tooltip = useRef<HTMLSpanElement>(null);
  const pointerInside = useRef(false);
  const focusInside = useRef(false);
  const dismissed = useRef(false);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<TooltipPosition | null>(null);
  const explanation = description || label;
  const descriptionIds = [describedBy, tooltipId].filter(Boolean).join(' ');
  const disabledExplanation = Boolean(disabled && description);
  // Stay in the modal's accessible subtree, even when using the browser top layer.
  const portalHost = open ? anchor.current?.closest('dialog[open]') || document.body : null;

  useLayoutEffect(() => {
    const element = tooltip.current;
    if (!open || !element) return;
    if (typeof element.showPopover === 'function' && element.hasAttribute('popover')) {
      try { element.showPopover(); } catch { element.removeAttribute('popover'); }
    }
    const motionRoot = anchor.current?.closest('[data-motion]');
    const syncMotion = () => { element.dataset.motion = motionRoot?.getAttribute('data-motion') || 'full'; };
    syncMotion();
    const motionObserver = motionRoot ? new MutationObserver(syncMotion) : null;
    if (motionRoot) motionObserver?.observe(motionRoot, { attributes: true, attributeFilter: ['data-motion'] });
    const update = () => {
      if (!anchor.current) return;
      const visual = window.visualViewport;
      const viewport = { left: visual?.offsetLeft || 0, top: visual?.offsetTop || 0, width: visual?.width || document.documentElement.clientWidth, height: visual?.height || window.innerHeight };
      const width = Math.max(0, Math.min(280, viewport.width - 16)), height = Math.max(0, viewport.height - 16);
      element.style.maxWidth = `${width}px`; element.style.maxHeight = `${height}px`;
      element.style.left = '0px'; element.style.top = '0px';
      const bounds = element.getBoundingClientRect();
      // A legacy browser may position fixed descendants relative to a transformed
      // dialog. Measure that origin and scale instead of assuming viewport zero.
      const scaleX = element.offsetWidth ? bounds.width / element.offsetWidth || 1 : 1;
      const scaleY = element.offsetHeight ? bounds.height / element.offsetHeight || 1 : 1;
      element.style.maxWidth = `${width / scaleX}px`; element.style.maxHeight = `${height / scaleY}px`;
      const origin = element.getBoundingClientRect();
      const desired = placeActionTooltip(anchor.current.getBoundingClientRect(), origin, viewport, tooltipSide);
      const next = { left: (desired.left - origin.left) / scaleX, top: (desired.top - origin.top) / scaleY, side: desired.side };
      element.style.left = `${next.left}px`; element.style.top = `${next.top}px`;
      setPosition(previous => previous?.left === next.left && previous.top === next.top && previous.side === next.side ? previous : next);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault(); event.stopPropagation();
      dismissed.current = true; setOpen(false); setPosition(null);
    };
    update();
    document.addEventListener('keydown', escape, true);
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    window.visualViewport?.addEventListener('resize', update);
    window.visualViewport?.addEventListener('scroll', update);
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update);
    if (anchor.current) observer?.observe(anchor.current);
    if (tooltip.current) observer?.observe(tooltip.current);
    return () => {
      document.removeEventListener('keydown', escape, true);
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
      window.visualViewport?.removeEventListener('resize', update);
      window.visualViewport?.removeEventListener('scroll', update);
      observer?.disconnect();
      motionObserver?.disconnect();
      if (typeof element.hidePopover === 'function' && element.matches(':popover-open')) element.hidePopover();
    };
  }, [open, description, label, tooltipSide, portalHost]);

  const explanationNode = <span id={tooltipId} role={open ? 'tooltip' : undefined} ref={open ? tooltip : undefined} className={open ? styles.tooltip : styles.srOnly} popover={open && typeof HTMLElement.prototype.showPopover === 'function' ? 'manual' : undefined} data-motion={anchor.current?.closest('[data-motion]')?.getAttribute('data-motion') || undefined} data-side={position?.side || tooltipSide} style={open ? { left: position?.left ?? 0, top: position?.top ?? 0, visibility: position ? 'visible' : 'hidden' } : undefined}>{open && description ? <><strong>{label}</strong><span>{description}</span></> : explanation}</span>;
  return <span ref={anchor} className={styles.anchor} tabIndex={disabledExplanation ? 0 : undefined} role={disabledExplanation ? 'group' : undefined} aria-label={disabledExplanation ? accessibleName || label : undefined} aria-describedby={disabledExplanation ? descriptionIds : undefined}
    onPointerEnter={event => { if (event.pointerType === 'touch') return; pointerInside.current = true; dismissed.current = false; setOpen(true); }}
    onPointerLeave={() => { pointerInside.current = false; if (!focusInside.current || dismissed.current) { setOpen(false); setPosition(null); } }}
    onFocus={() => { focusInside.current = true; dismissed.current = false; setOpen(true); }}
    onBlur={event => { if (event.relatedTarget instanceof Node && event.currentTarget.contains(event.relatedTarget)) return; focusInside.current = false; if (!pointerInside.current || dismissed.current) { setOpen(false); setPosition(null); } }}>
    <button {...buttonProps} ref={ref} type={type} disabled={disabled} aria-label={accessibleName || label} aria-describedby={descriptionIds} className={[styles.control, className].filter(Boolean).join(' ')} data-variant={variant} data-icon-only={!showLabel || undefined}>
      <RoomIcon name={icon} size={20}/>
      {showLabel && <span className={styles.label}>{children ?? label}</span>}
    </button>
    {portalHost ? createPortal(explanationNode, portalHost) : explanationNode}
  </span>;
});

export default ActionControl;
